// Beta-0.5B Phase 4: 「AIが検索できる情報」— canonical, single-source status.
// Reads exclusively from GET /api/ai/search/status which itself only reads
// from search_index (+ evidence privacy policy + latest crawler_jobs).
// Never reads kb_embeddings — chunks are a legacy per-doc-QA store and
// belong on the RAG page, not on the top-level "what the AI can search" page.

import { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowsClockwise, Info, CircleNotch, CheckCircle, Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '@/utils/datetime';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('ja-JP') : '—');
const fmtDT = (iso) => (iso ? formatDateTime(iso) : '—');

function MetricCard({ testid, label, value, definition, source }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white px-4 py-3 flex flex-col gap-1"
      data-testid={testid}>
      <div className="flex items-baseline justify-between">
        <p className="text-[12px] font-semibold text-zinc-700">{label}</p>
        <p className="text-[24px] tabular-nums font-semibold text-teal-700">{fmt(value)}</p>
      </div>
      <p className="text-[11px] text-zinc-500 leading-snug">{definition}</p>
      <p className="text-[10px] font-mono text-zinc-400 mt-0.5">
        出典: {source}
      </p>
    </div>
  );
}

export default function AiSearchStatusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/api/ai/search/status`, { headers: authHeader() });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'AI検索状況の取得に失敗しました');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const m = data?.metrics || {};
  const current = data?.current_job;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 space-y-5" data-testid="ai-search-status-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">AIが検索できる情報</h1>
          <p className="text-[12px] text-zinc-600 mt-1">
            この画面が唯一の「AIが今、社内のどれくらいの情報を検索できるか」を示す公式な数字です。
            すべての数字は canonical な検索索引（<span className="font-mono">search_index</span>）から計算しています。
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="h-9 text-[12px]"
          data-testid="ai-search-status-refresh">
          {loading ? <CircleNotch size={13} className="animate-spin mr-1" /> : <ArrowsClockwise size={13} className="mr-1" />}
          更新
        </Button>
      </div>

      {err && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800"
          data-testid="ai-search-status-error">
          <Warning size={13} className="inline mr-1" />
          {typeof err === 'string' ? err : (err.message || 'エラー')}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <MetricCard testid="metric-searchable"
              label="検索できる情報"
              value={m.searchable_records?.value}
              definition={m.searchable_records?.definition_ja}
              source={m.searchable_records?.source_collection} />
            <MetricCard testid="metric-with-vectors"
              label="意味検索に使える件数"
              value={m.records_with_semantic_vectors?.value}
              definition={m.records_with_semantic_vectors?.definition_ja}
              source={m.records_with_semantic_vectors?.source_collection} />
            <MetricCard testid="metric-awaiting"
              label="検索準備中の件数"
              value={m.records_awaiting_index?.value}
              definition={m.records_awaiting_index?.definition_ja}
              source={m.records_awaiting_index?.source_collection} />
            <MetricCard testid="metric-failed"
              label="検索エラーの件数"
              value={m.records_failed_index?.value}
              definition={m.records_failed_index?.definition_ja}
              source={m.records_failed_index?.source_collection} />
            <MetricCard testid="metric-excluded"
              label="機密除外により対象外"
              value={m.records_excluded_by_policy?.value}
              definition={m.records_excluded_by_policy?.definition_ja}
              source={m.records_excluded_by_policy?.source_collection} />
            <MetricCard testid="metric-rules"
              label="除外ルールの合計"
              value={m.exclusion_rule_count?.value}
              definition={m.exclusion_rule_count?.definition_ja}
              source={m.exclusion_rule_count?.source_collection} />
          </div>

          <div className="rounded-lg border border-zinc-200 bg-white px-4 py-3"
            data-testid="ai-search-status-context">
            <p className="text-[12px] font-semibold text-zinc-700 mb-2">最新の索引更新</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[12px]">
              <div>
                <p className="text-zinc-500">最後に成功した更新</p>
                <p className="text-zinc-900 font-medium" data-testid="last-successful-update">
                  {fmtDT(data.last_successful_update?.at)}
                  {data.last_successful_update?.kind ? (
                    <span className="ml-2 text-[10px] text-zinc-500 font-mono">
                      {data.last_successful_update.kind}
                    </span>
                  ) : null}
                </p>
              </div>
              <div>
                <p className="text-zinc-500">現在のジョブ</p>
                {current ? (
                  <p className="text-zinc-900 font-medium" data-testid="current-job">
                    <CircleNotch size={12} className="inline animate-spin mr-1" />
                    {current.kind} · {current.status}
                  </p>
                ) : (
                  <p className="text-zinc-500" data-testid="current-job-none">
                    <CheckCircle size={12} className="inline mr-1 text-emerald-600" />
                    実行中のジョブはありません
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-zinc-200 bg-white px-4 py-3"
            data-testid="ai-search-status-per-module">
            <p className="text-[12px] font-semibold text-zinc-700 mb-2">分類別の内訳</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1 text-[12px]">
              {Object.entries(data.per_module || {}).map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between border-b border-zinc-100 py-1"
                  data-testid={`per-module-${k}`}>
                  <span className="text-zinc-600 font-mono text-[11px]">{k}</span>
                  <span className="text-zinc-900 font-semibold tabular-nums">{fmt(v)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 text-[11px] text-zinc-600"
            data-testid="ai-search-status-model">
            <p className="flex items-center gap-1">
              <Info size={12} />
              <span>意味検索モデル: <span className="font-mono">{data.embedding_model?.name || '—'}</span>
                {data.embedding_model?.vector_dim ? ` · ${data.embedding_model.vector_dim}次元` : ''}
              </span>
            </p>
            <p className="mt-1">
              {data.embedding_model?.definition_ja}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
